"use client";

import Breadcrumb from "../common/breadcrumb/Breadcrumb";
import CoursePagination from "./CoursePagination";
import { memoCoursesType } from "@/interFace/courses/types";
import { Translations } from "@/translations";
import { use, useContext, useEffect, useState } from "react";
import Loading from "@/app/[locale]/Loading";
import { fetchWithAuth } from "@/lib/MemoBE/fetchWithAuth";
import { useTranslations } from "next-intl";
import { getLink, Routes } from "@/constants";
import { useRouter, useSearchParams } from "next/navigation";
import { mapTableSquareCoursePricing, TableSquarePricingResponse } from "@/lib/coursePricing";
import { AppContext } from "@/contextApi/AppProvider";
import { AppContextType } from "@/interFace/general";

interface MainProps {
  locale: string;
}

const CourseListMain: React.FC<MainProps> = ({ locale }) => {
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState<memoCoursesType[]>([]);
  const { userInfo } = useContext(AppContext) as AppContextType;
  const router = useRouter();
  const t = useTranslations("Courses");
  const tParents = useTranslations(Translations.Parents.Page);
  const searchParams = useSearchParams();
  const childName = searchParams.get("childName")?.trim() ?? "";
  const isTransferIntent = searchParams.get("intent") === "transfer";
  const isPurchaseRestricted = userInfo.ageGroup !== "16plus";
  const fallbackUrl = getLink(locale, Routes.DASHBOARD_COURSES);

  useEffect(() => {
    if (isPurchaseRestricted) {
      router.replace(fallbackUrl);
    }
  }, [fallbackUrl, isPurchaseRestricted, router]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const url = `/api/pubcourses`;
        const [coursesData, tableSquarePricingData] = await Promise.all([
          fetchWithAuth(url, "GET"),
          fetchWithAuth("/api/pricing/table-square", "GET", undefined, true).catch(() => null),
        ]);

        const tableSquarePricing = tableSquarePricingData as TableSquarePricingResponse | null;
        const normalizedCourses = Array.isArray(coursesData)
          ? (coursesData as memoCoursesType[]).map((course) =>
              mapTableSquareCoursePricing(course, tableSquarePricing)
            )
          : [];

        const visibleCourses = normalizedCourses.filter(
          (course) => course.unavailableReason !== "price-not-available"
        );

        setCourses(visibleCourses);
      } catch (error) {
        console.error("Error fetching courses details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <Loading />;
  }

  if (isPurchaseRestricted) {
    return <Loading />;
  }

  return (
    <>
      <Breadcrumb title={t(Translations.Courses.Title)} />
      <section className="course__area pt-40 pb-120">
        <div className="container">
          {childName && (
            <div
              className="mb-4"
              style={{
                padding: "18px 20px",
                borderRadius: 18,
                border: "1px solid #dbe6f3",
                background: "linear-gradient(135deg, #f8fbff 0%, #ffffff 100%)",
              }}
            >
              <div className="fw-semibold mb-1" style={{ color: "#28405d" }}>
                {isTransferIntent
                  ? tParents(Translations.Parents.ChildTransferContextTitle, { childName })
                  : tParents(Translations.Parents.ChildBrowseContextTitle, { childName })}
              </div>
              <div className="text-muted small">
                {isTransferIntent
                  ? tParents(Translations.Parents.ChildTransferContextDescription, { childName })
                  : tParents(Translations.Parents.ChildBrowseContextDescription, { childName })}
              </div>
            </div>
          )}
          <CoursePagination locale={locale} data={courses} />
        </div>
      </section>
    </>
  );
};

export default CourseListMain;
